import { useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Bandeau, Champ, Liste, Zone } from './Champs'
import { jour, montant } from './Bail'
import { ErreurApi } from '../api'
import {
  accepterReglement,
  agenda as chargerAgenda,
  ajouterArticle,
  ajouterDepense,
  annoncerDepart,
  annulerDepart,
  cocherArticle,
  confirmerReglement,
  convertirEnDepense,
  creerTache,
  declarerAbsence,
  departs as chargerDeparts,
  depenses as chargerDepenses,
  foyer as chargerFoyer,
  listeCoursesSiModifiee,
  listesCourses,
  mesFoyers,
  planning as chargerPlanning,
  proposerReglement,
  publierReglement,
  reglementInterieur as chargerReglement,
  repartition as chargerRepartition,
  retirerArticle,
  sauterOccurrence,
  soldes as chargerSoldes,
  terminerOccurrence,
  type Agenda,
  type Depart,
  type Depense,
  type Foyer as FoyerDto,
  type ListeCourses,
  type MembreFoyer,
  type Occurrence,
  type Planning,
  type Recurrence,
  type ReglementInterieur,
  type Repartition,
  type Soldes,
  type StrategieTache,
} from '../apiFoyer'
import { interpoler, messageErreur, TEXTES, type Langue } from '../i18n'
import { Bloc, Bouton, EtatVide, Icone, Modale, Onglets, Puce, SqueletteListe, SquelettePage } from '../ui'

type Onglet = 'taches' | 'depenses' | 'courses' | 'agenda' | 'reglement' | 'membres' | 'depart'
const ONGLETS: readonly Onglet[] = ['taches', 'depenses', 'courses', 'agenda', 'reglement', 'membres', 'depart']

type Textes = (typeof TEXTES)['fr']

/**
 * La vie du foyer — revue le 15/09/2026 sur la maquette `docs/ui/pwa/foyer.html`.
 *
 * <b>Le foyer est un lieu, pas sept rubriques de menu</b> (conception L7, §6) : une seule entrée de
 * navigation, sept onglets — tâches, dépenses, courses, agenda, règlement, membres, mon départ.
 * L'en-tête dit le nom, le nombre de membres, la date de naissance du foyer (la signature du
 * premier bail) et les avatars.
 *
 * <b>Chaque panneau est fait de cartes à en-tête</b> — un titre, une action — et de trois motifs que
 * L7 apporte au système : la liste cochable (tâches, courses, soldes), la jauge (la répartition, qui
 * montre tout le monde et ne désigne personne) et le tableau dense (les dépenses). Les quatre états
 * sont déclarés, et l'état vide est ici la règle : un foyer neuf a zéro tâche, zéro dépense, zéro
 * course.
 */
export function Foyer({ langue, moi }: { langue: Langue; moi: string | null }) {
  const t = TEXTES[langue]

  const [foyerId, setFoyerId] = useState<string | null>(null)
  const [detail, setDetail] = useState<FoyerDto | null>(null)
  // `?onglet=depart` : la fiche du bail y envoie « Annoncer mon départ ».
  const [parametres] = useSearchParams()
  const [onglet, setOnglet] = useState<Onglet>(() => {
    const demande = parametres.get('onglet')

    return demande !== null && ONGLETS.includes(demande as Onglet) ? (demande as Onglet) : 'taches'
  })
  const [erreur, setErreur] = useState<string | null>(null)
  const [aucun, setAucun] = useState(false)

  useEffect(() => {
    void mesFoyers()
      .then((liste) => {
        if (liste.length === 0) {
          setAucun(true)
          return
        }

        setFoyerId(liste[0].id)
      })
      .catch((cause: unknown) => setErreur(messageErreur(langue, code(cause))))
  }, [langue])

  const recharger = useCallback(() => {
    if (foyerId === null) {
      return
    }

    void chargerFoyer(foyerId)
      .then(setDetail)
      .catch((cause: unknown) => setErreur(messageErreur(langue, code(cause))))
  }, [foyerId, langue])

  useEffect(recharger, [recharger])

  if (erreur !== null && detail === null) {
    return (
      <section className="foyer">
        <div className="page-entete">
          <h2>{t.foyer}</h2>
        </div>
        <Bandeau ton="erreur">{erreur}</Bandeau>
      </section>
    )
  }

  if (aucun) {
    return (
      <section className="foyer">
        <div className="page-entete">
          <h2>{t.foyer}</h2>
        </div>
        <EtatVide icone="groupe" titre={t.foyerAucunTitre}>
          {t.foyerAucun}
        </EtatVide>
      </section>
    )
  }

  if (detail === null) {
    return (
      <section className="foyer">
        <div className="page-entete">
          <h2>{t.foyer}</h2>
        </div>
        <SqueletteListe />
      </section>
    )
  }

  const actifs = detail.members.filter((membre) => membre.status === 'Active' || membre.status === 'NoticeGiven')

  return (
    <section className="foyer">
      <div className="page-entete">
        <div className="page-entete__rangee">
          <div>
            <h2>{detail.name}</h2>
            <p className="secondaire">
              {interpoler(t.foyerEnTete, { n: actifs.length, date: jour(langue, detail.createdAt) }, langue)}
            </p>
          </div>
          <div className="rangee rangee--serree foyer__avatars" aria-hidden="true">
            {actifs.map((membre) => (
              <span key={membre.memberId} className={membre.userId === moi ? 'avatar avatar--sm avatar--marque' : 'avatar avatar--sm'}>
                {initiales(membre.displayName)}
              </span>
            ))}
          </div>
        </div>
        <Onglets<Onglet>
          ariaLabel={t.foyer}
          actif={onglet}
          onChange={setOnglet}
          onglets={[
            { cle: 'taches', libelle: t.foyerTaches },
            { cle: 'depenses', libelle: t.foyerDepenses },
            { cle: 'courses', libelle: t.foyerCourses },
            { cle: 'agenda', libelle: t.foyerAgenda },
            { cle: 'reglement', libelle: t.foyerReglement },
            { cle: 'membres', libelle: t.foyerMembres },
            { cle: 'depart', libelle: t.foyerDepart },
          ]}
        />
      </div>

      {erreur !== null && <Bandeau ton="erreur">{erreur}</Bandeau>}

      {onglet === 'taches' && <OngletTaches foyer={detail} langue={langue} moi={moi} />}
      {onglet === 'depenses' && <OngletDepenses foyer={detail} langue={langue} moi={moi} />}
      {onglet === 'courses' && <OngletCourses foyer={detail} langue={langue} moi={moi} />}
      {onglet === 'agenda' && <OngletAgenda foyer={detail} langue={langue} />}
      {onglet === 'reglement' && <OngletReglement foyer={detail} langue={langue} moi={moi} />}
      {onglet === 'membres' && <OngletMembres foyer={detail} langue={langue} moi={moi} />}
      {onglet === 'depart' && <OngletDepart foyer={detail} langue={langue} moi={moi} />}
    </section>
  )
}

// ---------------------------------------------------------------------------
// Tâches
// ---------------------------------------------------------------------------

/**
 * Le planning des deux semaines, et le graphe de répartition.
 *
 * <b>Le graphe montre tout le monde, jamais une personne</b> (conception L7, §1.4-c). La spec écrit
 * « X a réalisé 55 % » en ajoutant « sans jugement » : les deux ne tiennent pas ensemble dans une
 * phrase affichée à quatre colocataires, parce que nommer quelqu'un <b>est</b> le jugement.
 */
function OngletTaches({ foyer, langue, moi }: { foyer: FoyerDto; langue: Langue; moi: string | null }) {
  const t = TEXTES[langue]
  const [planning, setPlanning] = useState<Planning | null>(null)
  const [repartition, setRepartition] = useState<Repartition | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [creation, setCreation] = useState(false)

  const charger = useCallback(() => {
    const aujourdhui = new Date()
    const dans = new Date(aujourdhui.getTime() + 14 * 86_400_000)

    void Promise.all([chargerPlanning(foyer.id, iso(aujourdhui), iso(dans)), chargerRepartition(foyer.id)])
      .then(([p, r]) => {
        setPlanning(p)
        setRepartition(r)
      })
      .catch((cause: unknown) => setErreur(messageErreur(langue, code(cause))))
  }, [foyer.id, langue])

  useEffect(charger, [charger])

  if (erreur !== null) {
    return <Bandeau ton="erreur">{erreur}</Bandeau>
  }

  if (planning === null || repartition === null) {
    return <SquelettePage />
  }

  const total = Math.max(1, ...repartition.members.map((membre) => membre.completedCount))

  return (
    <div className="deux-colonnes">
      <Bloc
        titre={<h3>{t.foyerCetteSemaine}</h3>}
        actions={
          <Bouton apparence="secondaire" taille="sm" onClick={() => setCreation(true)}>
            <Icone nom="plus" taille="sm" /> {t.foyerAjouterTache}
          </Bouton>
        }
      >
        {planning.chores.length === 0 ? (
          <EtatVide icone="tache" titre={t.foyerTachesVideTitre}>
            {t.foyerTachesVide}
          </EtatVide>
        ) : planning.occurrences.length === 0 ? (
          <p className="secondaire">{t.foyerPlanningVide}</p>
        ) : (
          <ul className="liste-lignes">
            {planning.occurrences.map((occurrence) => (
              <LigneOccurrence key={occurrence.id} t={t} occurrence={occurrence} langue={langue} moi={moi} onFait={charger} />
            ))}
          </ul>
        )}
      </Bloc>

      {repartition.gamificationEnabled && (
        <Bloc titre={<h3>{t.foyerRepartition}</h3>}>
          <div className="pile">
            {repartition.members.map((membre) => (
              <div key={membre.userId} className="jauge">
                <div className="jauge__ligne">
                  <span>
                    {membre.displayName}
                    {membre.userId === moi && ` (${t.bailVous})`}
                  </span>
                  <span>{membre.completedCount === 1 ? t.foyerUneTache : interpoler(t.foyerNTaches, { n: membre.completedCount }, langue)}</span>
                </div>
                <div className="jauge__piste">
                  <div
                    className={repartition.isImbalanced && membre.sharePercent < 100 / repartition.members.length / 2 ? 'jauge__valeur jauge__valeur--attention' : 'jauge__valeur'}
                    style={{ inlineSize: `${(membre.completedCount / total) * 100}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          {repartition.isImbalanced && (
            <Bandeau ton="attention" icone={<Icone nom="alerte" />}>
              {t.foyerDesequilibre}
            </Bandeau>
          )}
        </Bloc>
      )}

      {creation && (
        <NouvelleTache
          t={t}
          foyer={foyer}
          langue={langue}
          onFermer={() => setCreation(false)}
          onCree={() => {
            setCreation(false)
            charger()
          }}
          onErreur={setErreur}
        />
      )}
    </div>
  )
}

function LigneOccurrence({
  t,
  occurrence,
  langue,
  moi,
  onFait,
}: {
  t: Textes
  occurrence: Occurrence
  langue: Langue
  moi: string | null
  onFait: () => void
}) {
  const [enCours, setEnCours] = useState(false)

  const close = occurrence.status === 'Done' || occurrence.status === 'Skipped'
  const aMoi = occurrence.assignedToUserId === moi

  let meta: string

  if (occurrence.status === 'Done') {
    meta = interpoler(t.foyerFaitPar, { nom: aMoi ? t.bailVous : (occurrence.assignedToDisplayName ?? '—') })
  } else if (occurrence.status === 'Skipped') {
    meta = t.foyerTourSaute
  } else if (occurrence.skippedForAbsence.length > 0) {
    meta = interpoler(t.foyerRepasseA, { nom: occurrence.assignedToDisplayName ?? '—' })
  } else {
    meta = aMoi
      ? interpoler(t.foyerVotreTour, { date: jour(langue, occurrence.dueOn) })
      : interpoler(t.foyerTourDe, { nom: occurrence.assignedToDisplayName ?? '—', date: jour(langue, occurrence.dueOn) })
  }

  return (
    <li className={close ? 'est-estompee' : undefined}>
      <input
        type="checkbox"
        className="foyer__case"
        checked={occurrence.status === 'Done'}
        disabled={close || enCours}
        aria-label={occurrence.choreTitle}
        onChange={() => {
          setEnCours(true)
          void terminerOccurrence(occurrence.id).then(onFait).finally(() => setEnCours(false))
        }}
      />
      <span className="liste-lignes__corps">
        <span className={occurrence.status === 'Done' ? 'liste-lignes__titre est-barre' : 'liste-lignes__titre'}>{occurrence.choreTitle}</span>
        <span className="liste-lignes__meta">
          {occurrence.status === 'Overdue' ? <Puce ton="danger">{t.foyerEnRetard}</Puce> : meta}
        </span>
      </span>
      {!close && (
        <Bouton apparence="discret" taille="sm" charge={enCours} onClick={() => {
          setEnCours(true)
          void sauterOccurrence(occurrence.id).then(onFait).finally(() => setEnCours(false))
        }}>
          {t.foyerSauter}
        </Bouton>
      )}
      {occurrence.assignedToDisplayName !== null && (
        <span className={aMoi ? 'avatar avatar--sm avatar--marque' : 'avatar avatar--sm'} title={occurrence.assignedToDisplayName} aria-hidden="true">
          {initiales(occurrence.assignedToDisplayName)}
        </span>
      )}
    </li>
  )
}

/** Une tâche : un titre, une récurrence, une stratégie. Le reste a une valeur par défaut. */
function NouvelleTache({
  t,
  foyer,
  langue,
  onFermer,
  onCree,
  onErreur,
}: {
  t: Textes
  foyer: FoyerDto
  langue: Langue
  onFermer: () => void
  onCree: () => void
  onErreur: (message: string) => void
}) {
  const [titre, setTitre] = useState('')
  const [recurrence, setRecurrence] = useState<Recurrence>('Weekly')
  const [strategie, setStrategie] = useState<StrategieTache>('Rotation')
  const [envoi, setEnvoi] = useState(false)

  const creer = async () => {
    if (titre.trim() === '') return

    setEnvoi(true)

    try {
      await creerTache(foyer.id, {
        title: titre.trim(),
        description: null,
        effortWeight: 1,
        recurrence,
        recurrenceInterval: 1,
        daysOfWeekMask: null,
        dayOfMonth: null,
        strategy: strategie,
        fixedAssigneeUserId: null,
        requiresPhotoProof: false,
        startsOn: iso(new Date()),
      })
      onCree()
    } catch (cause: unknown) {
      onErreur(messageErreur(langue, code(cause)))
      setEnvoi(false)
    }
  }

  return (
    <Modale
      feuille
      titre={t.foyerAjouterTache}
      onFermer={onFermer}
      pied={
        <>
          <Bouton apparence="discret" onClick={onFermer}>
            {t.annuler}
          </Bouton>
          <Bouton charge={envoi} desactive={titre.trim() === ''} onClick={() => void creer()}>
            {t.foyerAjouter}
          </Bouton>
        </>
      }
    >
      <Champ id="tache-titre" libelle={t.foyerTacheTitre} valeur={titre} requis onChange={setTitre} />
      <Liste
        id="tache-recurrence"
        libelle={t.foyerTacheRecurrence}
        valeur={recurrence}
        options={[
          { valeur: 'Daily', libelle: t.foyerRecurrenceDaily },
          { valeur: 'Weekly', libelle: t.foyerRecurrenceWeekly },
          { valeur: 'BiWeekly', libelle: t.foyerRecurrenceBiWeekly },
          { valeur: 'Monthly', libelle: t.foyerRecurrenceMonthly },
          { valeur: 'None', libelle: t.foyerRecurrenceNone },
        ]}
        onChange={(valeur) => setRecurrence(valeur as Recurrence)}
      />
      <Liste
        id="tache-strategie"
        libelle={t.foyerTacheStrategie}
        valeur={strategie}
        aide={t.foyerTacheStrategieAide}
        options={[
          { valeur: 'Rotation', libelle: t.foyerStrategieRotation },
          { valeur: 'Volunteer', libelle: t.foyerStrategieVolunteer },
        ]}
        onChange={(valeur) => setStrategie(valeur as StrategieTache)}
      />
    </Modale>
  )
}

// ---------------------------------------------------------------------------
// Dépenses et soldes
// ---------------------------------------------------------------------------

/**
 * Dépenses, soldes, et le plan de remboursement le plus court.
 *
 * <b>Chaque transfert proposé porte les deux soldes qui le justifient</b> (conception L7, §1.6) : un
 * chiffre portant sur l'argent de quelqu'un doit pouvoir se défendre ligne à ligne. Deux
 * déclarations, comme pour le loyer : aucun mouvement d'argent ne passe par la plateforme.
 */
function OngletDepenses({ foyer, langue, moi }: { foyer: FoyerDto; langue: Langue; moi: string | null }) {
  const t = TEXTES[langue]
  const [liste, setListe] = useState<Depense[] | null>(null)
  const [soldes, setSoldes] = useState<Soldes | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [ajout, setAjout] = useState(false)
  const [occupe, setOccupe] = useState<string | null>(null)

  const charger = useCallback(() => {
    void Promise.all([chargerDepenses(foyer.id), chargerSoldes(foyer.id)])
      .then(([page, s]) => {
        setListe(page.items)
        setSoldes(s)
      })
      .catch((cause: unknown) => setErreur(messageErreur(langue, code(cause))))
  }, [foyer.id, langue])

  useEffect(charger, [charger])

  const agir = async (cle: string, action: () => Promise<unknown>) => {
    setOccupe(cle)

    try {
      await action()
      charger()
    } catch (cause: unknown) {
      setErreur(messageErreur(langue, code(cause)))
    } finally {
      setOccupe(null)
    }
  }

  if (erreur !== null) {
    return <Bandeau ton="erreur">{erreur}</Bandeau>
  }

  if (liste === null || soldes === null) {
    return <SquelettePage />
  }

  return (
    <div className="deux-colonnes">
      <Bloc
        titre={<h3>{t.foyerDepensesPartagees}</h3>}
        actions={
          <Bouton apparence="secondaire" taille="sm" onClick={() => setAjout(true)}>
            <Icone nom="plus" taille="sm" /> {t.foyerAjouter}
          </Bouton>
        }
      >
        {liste.length === 0 ? (
          <p className="secondaire">{t.foyerDepensesVide}</p>
        ) : (
          <div className="table-defilante table-defilante--nue">
            <table>
              <caption className="visuellement-cachee">{t.foyerDepenses}</caption>
              <thead>
                <tr>
                  <th scope="col">{t.foyerDepenseIntitule}</th>
                  <th scope="col">{t.foyerPayePar}</th>
                  <th scope="col" className="nombre">
                    {t.foyerDepenseMontant}
                  </th>
                  <th scope="col">{t.foyerDate}</th>
                </tr>
              </thead>
              <tbody>
                {liste.map((depense) => (
                  <tr key={depense.id} className={depense.needsAmountConfirmation ? 'est-estompee' : undefined}>
                    <td>{depense.title}</td>
                    <td>{depense.paidByUserId === moi ? t.bailVousMajuscule : depense.paidByDisplayName}</td>
                    <td className="nombre">
                      <bdi>{montant(langue, depense.amount, depense.currencyCode)}</bdi>
                    </td>
                    <td className="secondaire">{jour(langue, depense.spentOn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Bloc>

      <Bloc titre={<h3>{t.foyerSoldes}</h3>}>
        <ul className="liste-lignes">
          {soldes.balances.map((solde) => (
            <li key={solde.userId}>
              <span className={solde.userId === moi ? 'avatar avatar--sm avatar--marque' : 'avatar avatar--sm'} aria-hidden="true">
                {initiales(solde.displayName)}
              </span>
              <span className="liste-lignes__corps">
                <span className="liste-lignes__titre">
                  {solde.displayName}
                  {solde.userId === moi && ` (${t.bailVous})`}
                </span>
              </span>
              <span className={solde.net < 0 ? 'fort foyer__solde foyer__solde--debiteur' : 'fort foyer__solde foyer__solde--crediteur'}>
                {solde.net < 0 ? '− ' : '+ '}
                <bdi>{montant(langue, Math.abs(solde.net), solde.currencyCode)}</bdi>
              </span>
            </li>
          ))}
        </ul>

        {(soldes.suggestedTransfers.length > 0 || soldes.pendingSettlements.length > 0) && (
          <>
            <h4 className="foyer__sous-titre">{t.foyerTransferts}</h4>
            <ul className="liste-lignes">
              {soldes.suggestedTransfers.map((transfert) => {
                const cle = `${transfert.fromUserId}-${transfert.toUserId}`

                return (
                  <li key={cle}>
                    <span className="liste-lignes__corps">
                      <span className="liste-lignes__titre">
                        {transfert.fromUserId === moi ? t.bailVousMajuscule : transfert.fromDisplayName}
                        {' → '}
                        {transfert.toUserId === moi ? t.bailVous : transfert.toDisplayName}
                      </span>
                      <span className="liste-lignes__meta">
                        <bdi>{montant(langue, transfert.amount, transfert.currencyCode)}</bdi>
                      </span>
                    </span>
                    {transfert.fromUserId === moi && (
                      <Bouton
                        taille="sm"
                        charge={occupe === cle}
                        onClick={() =>
                          void agir(cle, () =>
                            proposerReglement(foyer.id, {
                              fromUserId: transfert.fromUserId,
                              toUserId: transfert.toUserId,
                              amount: transfert.amount,
                              method: 'Cash',
                              settledOn: iso(new Date()),
                            }),
                          )
                        }
                      >
                        {t.foyerDeclarerRemboursement}
                      </Bouton>
                    )}
                  </li>
                )
              })}

              {/* La double validation : on ne confirme jamais ce qu'on a déclaré soi-même. */}
              {soldes.pendingSettlements.map((reglement) => (
                <li key={reglement.id}>
                  <span className="liste-lignes__corps">
                    <span className="liste-lignes__titre">
                      {reglement.fromUserId === moi ? t.bailVousMajuscule : reglement.fromDisplayName}
                      {' → '}
                      {reglement.toUserId === moi ? t.bailVous : reglement.toDisplayName}
                    </span>
                    <span className="liste-lignes__meta">
                      <bdi>{montant(langue, reglement.amount, reglement.currencyCode)}</bdi>
                      {' · '}
                      {reglement.declaredByUserId === moi ? t.foyerDeclareEnAttente : t.foyerDeclareAConfirmer}
                    </span>
                  </span>
                  {reglement.declaredByUserId !== moi && (reglement.fromUserId === moi || reglement.toUserId === moi) && (
                    <Bouton
                      apparence="secondaire"
                      taille="sm"
                      charge={occupe === reglement.id}
                      onClick={() => void agir(reglement.id, () => confirmerReglement(reglement.id))}
                    >
                      {t.foyerConfirmer}
                    </Bouton>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}

        <p className="tertiaire foyer__note">{t.foyerDeuxDeclarations}</p>
      </Bloc>

      {ajout && (
        <NouvelleDepense
          t={t}
          foyer={foyer}
          langue={langue}
          onFermer={() => setAjout(false)}
          onCree={() => {
            setAjout(false)
            charger()
          }}
          onErreur={setErreur}
        />
      )}
    </div>
  )
}

function NouvelleDepense({
  t,
  foyer,
  langue,
  onFermer,
  onCree,
  onErreur,
}: {
  t: Textes
  foyer: FoyerDto
  langue: Langue
  onFermer: () => void
  onCree: () => void
  onErreur: (message: string) => void
}) {
  const [titre, setTitre] = useState('')
  const [somme, setSomme] = useState('')
  const [date, setDate] = useState(() => iso(new Date()))
  const [envoi, setEnvoi] = useState(false)

  const valeur = Number.parseFloat(somme.replace(',', '.'))
  const valide = titre.trim() !== '' && !Number.isNaN(valeur) && valeur > 0 && date !== ''

  const creer = async () => {
    if (!valide) return

    setEnvoi(true)

    try {
      await ajouterDepense(foyer.id, {
        title: titre.trim(),
        categoryCode: 'other',
        amount: valeur,
        spentOn: date,
        splitMethod: 'Equal',
      })
      onCree()
    } catch (cause: unknown) {
      onErreur(messageErreur(langue, code(cause)))
      setEnvoi(false)
    }
  }

  return (
    <Modale
      feuille
      titre={t.foyerAjouterDepense}
      onFermer={onFermer}
      pied={
        <>
          <Bouton apparence="discret" onClick={onFermer}>
            {t.annuler}
          </Bouton>
          <Bouton charge={envoi} desactive={!valide} onClick={() => void creer()}>
            {t.foyerAjouter}
          </Bouton>
        </>
      }
    >
      <Champ id="depense-titre" libelle={t.foyerDepenseIntitule} valeur={titre} requis onChange={setTitre} />
      <Champ id="depense-montant" libelle={t.foyerDepenseMontant} valeur={somme} suffixe={foyer.currencyCode} onChange={setSomme} />
      <Champ id="depense-date" type="date" libelle={t.foyerDate} valeur={date} onChange={setDate} />
      <p className="tertiaire">{t.foyerDepensePartsEgales}</p>
    </Modale>
  )
}

// ---------------------------------------------------------------------------
// Courses — le contrat de fraîcheur
// ---------------------------------------------------------------------------

/** Toutes les quatre secondes quand l'écran est visible, toutes les trente sinon. */
const RAFRAICHISSEMENT_ACTIF_MS = 4_000
const RAFRAICHISSEMENT_INACTIF_MS = 30_000

/**
 * La liste de courses partagée — <b>le seul endroit du produit où la fraîcheur se voit</b>.
 *
 * <b>C'est ici que vit D29</b> (conception L7, §1.1 et §3.4). Pas de connexion persistante : une
 * lecture conditionnelle toutes les quatre secondes quand l'écran est visible et au premier plan,
 * toutes les trente secondes sinon, et <b>rien du tout</b> quand l'onglet est caché. Une liste
 * inchangée répond `304` en quelques centaines d'octets.
 *
 * <b>Écriture optimiste</b> : cocher met à jour l'affichage immédiatement, et la réponse du serveur
 * fait autorité. Au supermarché, une case qui attend un aller-retour réseau est une case qu'on
 * coche deux fois.
 */
function OngletCourses({ foyer, langue, moi }: { foyer: FoyerDto; langue: Langue; moi: string | null }) {
  const t = TEXTES[langue]
  const [liste, setListe] = useState<ListeCourses | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [libelle, setLibelle] = useState('')
  const version = useRef<number | null>(null)

  useEffect(() => {
    void listesCourses(foyer.id)
      .then((listes) => {
        if (listes.length > 0) {
          setListe(listes[0])
          version.current = listes[0].version
        }
      })
      .catch((cause: unknown) => setErreur(messageErreur(langue, code(cause))))
  }, [foyer.id, langue])

  const listeId = liste?.id ?? null

  useEffect(() => {
    if (listeId === null) {
      return
    }

    let arrete = false

    const relire = async () => {
      if (arrete || document.visibilityState !== 'visible') {
        return
      }

      try {
        const fraiche = await listeCoursesSiModifiee(listeId, version.current)

        // `null` veut dire 304 : rien n'a changé, et on n'a rien téléchargé. C'est tout
        // l'intérêt du contrat — sans lui, ce serait la liste entière toutes les 4 secondes.
        if (fraiche !== null && !arrete) {
          version.current = fraiche.version
          setListe(fraiche)
        }
      } catch {
        // Une lecture ratée n'efface pas l'écran : la suivante arrive dans quatre secondes.
      }
    }

    const cadence = () =>
      document.visibilityState === 'visible' ? RAFRAICHISSEMENT_ACTIF_MS : RAFRAICHISSEMENT_INACTIF_MS

    let minuterie = window.setInterval(() => void relire(), cadence())

    const surVisibilite = () => {
      window.clearInterval(minuterie)
      minuterie = window.setInterval(() => void relire(), cadence())

      // Revenir sur l'onglet relit tout de suite : c'est le moment où l'écart se voit.
      void relire()
    }

    document.addEventListener('visibilitychange', surVisibilite)

    return () => {
      arrete = true
      window.clearInterval(minuterie)
      document.removeEventListener('visibilitychange', surVisibilite)
    }
  }, [listeId])

  if (erreur !== null) {
    return <Bandeau ton="erreur">{erreur}</Bandeau>
  }

  if (liste === null) {
    return <SquelettePage />
  }

  const appliquer = (promesse: Promise<{ version: number }>) => {
    promesse
      .then((reponse) => {
        version.current = reponse.version
      })
      .catch((cause: unknown) => setErreur(messageErreur(langue, code(cause))))
  }

  const nomMembre = (userId: string): string => {
    if (userId === moi) return t.bailVous

    return foyer.members.find((membre) => membre.userId === userId)?.displayName ?? '—'
  }

  const coches = liste.items.filter((article) => article.isChecked)

  return (
    <div className="foyer__colonne">
      <Bloc
        titre={<h3>{t.foyerListeCourses}</h3>}
        actions={<Puce>{liste.items.length === 1 ? t.foyerUnArticle : interpoler(t.foyerNArticles, { n: liste.items.length }, langue)}</Puce>}
      >
        <form
          className="champ__groupe foyer__ajout"
          onSubmit={(evenement) => {
            evenement.preventDefault()

            if (libelle.trim() === '') {
              return
            }

            const texte = libelle.trim()
            setLibelle('')

            // Écriture optimiste : l'article apparaît avant la réponse du serveur.
            setListe((courante) =>
              courante === null
                ? courante
                : {
                    ...courante,
                    items: [
                      ...courante.items,
                      {
                        id: `optimiste-${texte}`,
                        label: texte,
                        quantity: null,
                        addedByUserId: moi ?? '',
                        isChecked: false,
                        checkedByUserId: null,
                        checkedAt: null,
                        sortOrder: courante.items.length,
                      },
                    ],
                  },
            )

            appliquer(ajouterArticle(liste.id, texte, null))
          }}
        >
          <input
            type="text"
            className="saisie"
            value={libelle}
            onChange={(evenement) => setLibelle(evenement.target.value)}
            placeholder={t.foyerAjouterArticle}
            aria-label={t.foyerArticle}
          />
          <Bouton type="submit">{t.foyerAjouter}</Bouton>
        </form>

        {liste.items.length === 0 ? (
          <p className="secondaire">{t.foyerCoursesVide}</p>
        ) : (
          <ul className="liste-lignes">
            {liste.items.map((article) => (
              <li key={article.id}>
                <input
                  type="checkbox"
                  className="foyer__case"
                  checked={article.isChecked}
                  aria-label={article.label}
                  onChange={() => {
                    setListe((courante) =>
                      courante === null
                        ? courante
                        : {
                            ...courante,
                            items: courante.items.map((item) =>
                              item.id === article.id ? { ...item, isChecked: !item.isChecked } : item,
                            ),
                          },
                    )

                    appliquer(cocherArticle(article.id, !article.isChecked))
                  }}
                />
                <span className="liste-lignes__corps">
                  <span className={article.isChecked ? 'liste-lignes__titre est-barre' : 'liste-lignes__titre'}>
                    {article.label}
                    {article.quantity !== null && ` · ${article.quantity}`}
                  </span>
                  <span className="liste-lignes__meta">
                    {article.isChecked && article.checkedByUserId !== null
                      ? interpoler(t.foyerAchetePar, { nom: nomMembre(article.checkedByUserId) })
                      : article.addedByUserId !== ''
                        ? interpoler(t.foyerAjoutePar, { nom: nomMembre(article.addedByUserId) })
                        : ''}
                  </span>
                </span>
                <Bouton
                  apparence="discret"
                  taille="sm"
                  onClick={() => {
                    setListe((courante) =>
                      courante === null
                        ? courante
                        : { ...courante, items: courante.items.filter((item) => item.id !== article.id) },
                    )

                    appliquer(retirerArticle(article.id))
                  }}
                >
                  {t.foyerRetirer}
                </Bouton>
              </li>
            ))}
          </ul>
        )}

        {coches.length > 0 && (
          <ConversionEnDepense
            t={t}
            liste={liste}
            langue={langue}
            devise={foyer.currencyCode}
            nombre={coches.length}
            onConverti={() => {
              version.current = null
              void listesCourses(foyer.id).then((listes) => {
                if (listes.length > 0) {
                  setListe(listes[0])
                  version.current = listes[0].version
                }
              })
            }}
          />
        )}
      </Bloc>
    </div>
  )
}

/** « J'ai payé les courses » : le seul pont entre l'onglet Courses et l'onglet Dépenses. */
function ConversionEnDepense({
  t,
  liste,
  langue,
  devise,
  nombre,
  onConverti,
}: {
  t: Textes
  liste: ListeCourses
  langue: Langue
  devise: string
  nombre: number
  onConverti: () => void
}) {
  const [somme, setSomme] = useState('')
  const valeur = Number.parseFloat(somme.replace(',', '.'))

  return (
    <form
      className="bloc bloc--sourd foyer__conversion"
      onSubmit={(evenement) => {
        evenement.preventDefault()

        if (Number.isNaN(valeur) || valeur <= 0) {
          return
        }

        void convertirEnDepense(liste.id, {
          amount: valeur,
          categoryCode: 'groceries',
          spentOn: iso(new Date()),
        }).then(() => {
          setSomme('')
          onConverti()
        })
      }}
    >
      <p className="secondaire">{interpoler(t.foyerConvertirAide, { n: nombre }, langue)}</p>
      <div className="champ__groupe">
        <input
          type="text"
          className="saisie"
          inputMode="decimal"
          value={somme}
          onChange={(evenement) => setSomme(evenement.target.value)}
          placeholder={`${t.foyerDepenseMontant} (${devise})`}
          aria-label={t.foyerDepenseMontant}
        />
        <Bouton type="submit" apparence="secondaire" desactive={Number.isNaN(valeur) || valeur <= 0}>
          {t.foyerConvertir}
        </Bouton>
      </div>
    </form>
  )
}

// ---------------------------------------------------------------------------
// Agenda
// ---------------------------------------------------------------------------

/**
 * L'agenda : <b>deux sources réunies à la lecture</b> (conception L7, §3.3).
 *
 * Les absences ne sont pas des entrées d'agenda : elles ont leur propre table, celle que la
 * rotation lit. Les afficher ensemble est un choix d'écran, pas de modèle — et c'est ce qui évite
 * qu'un membre pose ses vacances quelque part où l'algorithme ne les verrait pas.
 */
function OngletAgenda({ foyer, langue }: { foyer: FoyerDto; langue: Langue }) {
  const t = TEXTES[langue]
  const [agenda, setAgenda] = useState<Agenda | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [debut, setDebut] = useState('')
  const [fin, setFin] = useState('')
  const [envoi, setEnvoi] = useState(false)

  const charger = useCallback(() => {
    void chargerAgenda(foyer.id)
      .then(setAgenda)
      .catch((cause: unknown) => setErreur(messageErreur(langue, code(cause))))
  }, [foyer.id, langue])

  useEffect(charger, [charger])

  if (erreur !== null) {
    return <Bandeau ton="erreur">{erreur}</Bandeau>
  }

  if (agenda === null) {
    return <SquelettePage />
  }

  const entrees = [
    ...agenda.events.map((evenement) => ({ cle: evenement.id, quand: evenement.startsAt, titre: evenement.title, meta: jour(langue, evenement.startsAt) })),
    ...agenda.absences.map((absence) => ({
      cle: absence.id,
      quand: absence.startsOn,
      titre: interpoler(t.foyerAbsenceDe, { nom: absence.displayName }),
      meta: interpoler(t.foyerAbsenceDuAu, { du: jour(langue, absence.startsOn), au: jour(langue, absence.endsOn) }),
    })),
  ].sort((a, b) => a.quand.localeCompare(b.quand))

  return (
    <div className="deux-colonnes">
      <Bloc titre={<h3>{t.foyerDeuxSemaines}</h3>}>
        {entrees.length === 0 ? (
          <p className="secondaire">{t.foyerAgendaVide}</p>
        ) : (
          <ul className="liste-lignes">
            {entrees.map((entree) => (
              <li key={entree.cle}>
                <span className="liste-lignes__corps">
                  <span className="liste-lignes__titre">{entree.titre}</span>
                  <span className="liste-lignes__meta">{entree.meta}</span>
                </span>
              </li>
            ))}
          </ul>
        )}
      </Bloc>

      <Bloc titre={<h3>{t.foyerAbsences}</h3>}>
        <form
          className="pile"
          onSubmit={(evenement) => {
            evenement.preventDefault()

            if (debut === '' || fin === '') {
              return
            }

            setEnvoi(true)

            void declarerAbsence(foyer.id, debut, fin, null)
              .then(() => {
                setDebut('')
                setFin('')
                charger()
              })
              .catch((cause: unknown) => setErreur(messageErreur(langue, code(cause))))
              .finally(() => setEnvoi(false))
          }}
        >
          <p className="secondaire">{t.foyerAbsenceAide}</p>
          <div className="formulaire formulaire--deux foyer__dates">
            <Champ id="absence-du" type="date" libelle={t.foyerDu} valeur={debut} onChange={setDebut} />
            <Champ id="absence-au" type="date" libelle={t.foyerAu} valeur={fin} onChange={setFin} />
          </div>
          <div>
            <Bouton type="submit" apparence="secondaire" taille="sm" charge={envoi} desactive={debut === '' || fin === ''}>
              {t.foyerDeclarerAbsence}
            </Bouton>
          </div>
        </form>
      </Bloc>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Règlement intérieur
// ---------------------------------------------------------------------------

/**
 * Le règlement intérieur — <b>une acceptation, jamais une signature</b>.
 *
 * Conception L7, §1.9. Un règlement entre colocataires n'engage personne devant un tribunal, et la
 * plateforme n'est partie à rien (spec §9.2). Les libellés disent « accepter » et « accepté le »,
 * partout, et il n'y a nulle part de « signature électronique ».
 */
function OngletReglement({ foyer, langue, moi }: { foyer: FoyerDto; langue: Langue; moi: string | null }) {
  const t = TEXTES[langue]
  const [reglement, setReglement] = useState<ReglementInterieur | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [texte, setTexte] = useState('')
  const [redige, setRedige] = useState(false)
  const [occupe, setOccupe] = useState(false)

  const charger = useCallback(() => {
    void chargerReglement(foyer.id)
      .then(setReglement)
      .catch((cause: unknown) => setErreur(messageErreur(langue, code(cause))))
  }, [foyer.id, langue])

  useEffect(charger, [charger])

  if (erreur !== null) {
    return <Bandeau ton="erreur">{erreur}</Bandeau>
  }

  if (reglement === null) {
    return <SquelettePage />
  }

  const estReferent =
    foyer.members.find((membre) => membre.userId === moi)?.role === 'Referent' ||
    !foyer.members.some((membre) => membre.role === 'Referent' && membre.status === 'Active')
  const publiePar = foyer.members.find((membre) => membre.userId === reglement.publishedByUserId)?.displayName
  const acceptants = reglement.acceptances.filter((acceptation) => acceptation.acceptedAt !== null)
  const derniere = acceptants.map((acceptation) => acceptation.acceptedAt!).sort().at(-1)

  const agir = async (action: () => Promise<unknown>) => {
    setOccupe(true)

    try {
      await action()
      setTexte('')
      setRedige(false)
      charger()
    } catch (cause: unknown) {
      setErreur(messageErreur(langue, code(cause)))
    } finally {
      setOccupe(false)
    }
  }

  return (
    <div className="foyer__colonne foyer__colonne--large">
      <Bloc
        titre={
          <div>
            <h3>{t.foyerReglementInterieur}</h3>
            {reglement.rulesId !== null && reglement.publishedAt !== null && (
              <p className="secondaire">
                {interpoler(t.foyerReglementPublie, {
                  version: reglement.version,
                  date: jour(langue, reglement.publishedAt),
                  nom: publiePar ?? '—',
                }, langue)}
              </p>
            )}
          </div>
        }
        actions={
          reglement.rulesId === null ? undefined : reglement.acceptedByMe ? (
            <Puce ton="succes">{t.foyerAccepteParVous}</Puce>
          ) : (
            <Puce ton="attention">{t.foyerNonAccepte}</Puce>
          )
        }
      >
        <div className="pile">
          {reglement.rulesId === null ? (
            <p className="secondaire">{t.foyerReglementVide}</p>
          ) : (
            <>
              <div className="foyer__reglement">{reglement.content}</div>

              <div className="rangee rangee--espace">
                <p className="secondaire">
                  {acceptants.length === 0
                    ? t.foyerPersonneAccepte
                    : interpoler(t.foyerAccepteParListe, {
                        noms: acceptants.map((acceptation) => acceptation.displayName).join(', '),
                        date: derniere === undefined ? '' : jour(langue, derniere),
                      })}
                </p>
                <div className="rangee rangee--serree">
                  {!reglement.acceptedByMe && (
                    <Bouton charge={occupe} onClick={() => void agir(() => accepterReglement(reglement.rulesId!))}>
                      {t.foyerAccepter}
                    </Bouton>
                  )}
                  {estReferent && !redige && (
                    <Bouton apparence="secondaire" taille="sm" onClick={() => setRedige(true)}>
                      {t.foyerPublierVersion}
                    </Bouton>
                  )}
                </div>
              </div>
            </>
          )}

          {reglement.rulesId === null && estReferent && !redige && (
            <div>
              <Bouton apparence="secondaire" onClick={() => setRedige(true)}>
                {t.foyerPublierVersion}
              </Bouton>
            </div>
          )}

          {estReferent && redige && (
            <form
              className="pile"
              onSubmit={(evenement) => {
                evenement.preventDefault()

                if (texte.trim() === '') {
                  return
                }

                void agir(() => publierReglement(foyer.id, texte.trim(), langue))
              }}
            >
              <Zone id="reglement-texte" libelle={t.foyerReglement} valeur={texte} lignes={10} onChange={setTexte} />
              <div className="rangee rangee--serree">
                <Bouton type="submit" charge={occupe} desactive={texte.trim() === ''}>
                  {t.foyerPublierVersion}
                </Bouton>
                <Bouton apparence="discret" onClick={() => setRedige(false)}>
                  {t.annuler}
                </Bouton>
              </div>
            </form>
          )}

          <Bandeau ton="info" icone={<Icone nom="info" />}>
            {t.foyerPublierAide}
          </Bandeau>
        </div>
      </Bloc>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Membres
// ---------------------------------------------------------------------------

function OngletMembres({ foyer, langue, moi }: { foyer: FoyerDto; langue: Langue; moi: string | null }) {
  const t = TEXTES[langue]
  const membres = foyer.members.filter((membre) => membre.status === 'Active' || membre.status === 'NoticeGiven')
  const pourcent = new Intl.NumberFormat(langue, { style: 'percent', maximumFractionDigits: 0 })

  return (
    <div className="foyer__colonne">
      <Bloc titre={<h3>{t.foyerMembresDuFoyer}</h3>}>
        <ul className="liste-lignes">
          {membres.map((membre) => (
            <li key={membre.memberId}>
              <span className={membre.userId === moi ? 'avatar avatar--marque' : 'avatar'} aria-hidden="true">
                {initiales(membre.displayName)}
              </span>
              <span className="liste-lignes__corps">
                <span className="liste-lignes__titre">
                  {membre.displayName}
                  {membre.userId === moi && ` — ${t.bailVous}`}
                </span>
                <span className="liste-lignes__meta">{metaMembre(t, langue, membre, pourcent)}</span>
              </span>
              {membre.status === 'NoticeGiven' && <Puce ton="attention">{t.foyerPreavisEnCours}</Puce>}
            </li>
          ))}
        </ul>
      </Bloc>
    </div>
  )
}

/** « Référente du foyer · depuis le 01/11/2026 · chambre 2 · 50 % » — chaque morceau s'il existe. */
function metaMembre(t: Textes, langue: Langue, membre: MembreFoyer, pourcent: Intl.NumberFormat): string {
  const morceaux: string[] = []

  if (membre.role === 'Referent') morceaux.push(t.foyerReferent)
  morceaux.push(interpoler(t.foyerDepuisLe, { date: jour(langue, membre.joinedOn) }))
  if (membre.unitLabel !== null) morceaux.push(membre.unitLabel)
  if (membre.rentShare !== null) morceaux.push(pourcent.format(membre.rentShare))

  return morceaux.join(' · ')
}

// ---------------------------------------------------------------------------
// Mon départ
// ---------------------------------------------------------------------------

/**
 * Le parcours de départ.
 *
 * <b>On annonce son propre départ, et rien d'autre</b> (conception L7, §1.10-a) : aucun champ ne
 * désigne quelqu'un d'autre. Un bailleur résilie un bail ; des colocataires n'expulsent personne.
 *
 * <b>La remise en annonce crée un brouillon, elle ne publie pas</b> : c'est le propriétaire qui
 * décide, et l'écran le dit plutôt que de laisser croire à une publication automatique.
 */
function OngletDepart({ foyer, langue, moi }: { foyer: FoyerDto; langue: Langue; moi: string | null }) {
  const t = TEXTES[langue]
  const [departs, setDeparts] = useState<Depart[] | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)
  const [date, setDate] = useState('')
  const [occupe, setOccupe] = useState(false)

  const charger = useCallback(() => {
    void chargerDeparts(foyer.id)
      .then(setDeparts)
      .catch((cause: unknown) => setErreur(messageErreur(langue, code(cause))))
  }, [foyer.id, langue])

  useEffect(charger, [charger])

  if (erreur !== null) {
    return <Bandeau ton="erreur">{erreur}</Bandeau>
  }

  if (departs === null) {
    return <SquelettePage />
  }

  const monDepart = departs.find(
    (depart) => depart.memberUserId === moi && depart.status !== 'Completed' && depart.status !== 'Cancelled',
  )
  const auPlusTot = new Date(Date.now() + foyer.noticePeriodDays * 86_400_000)

  const agir = async (action: () => Promise<unknown>) => {
    setOccupe(true)

    try {
      await action()
      setDate('')
      charger()
    } catch (cause: unknown) {
      setErreur(messageErreur(langue, code(cause)))
    } finally {
      setOccupe(false)
    }
  }

  return (
    <div className="foyer__colonne">
      <Bloc titre={<h3>{t.foyerAnnoncerDepart}</h3>}>
        {monDepart === undefined ? (
          <form
            className="pile"
            onSubmit={(evenement) => {
              evenement.preventDefault()

              if (date !== '') {
                void agir(() => annoncerDepart(foyer.id, date, true))
              }
            }}
          >
            <p className="secondaire">{t.foyerDepartAide}</p>
            <div className="foyer__date">
              <Champ
                id="depart-date"
                type="date"
                libelle={t.foyerDepartDate}
                valeur={date}
                aide={interpoler(t.foyerDepartPreavis, { n: foyer.noticePeriodDays, date: jour(langue, iso(auPlusTot)) }, langue)}
                onChange={setDate}
              />
            </div>
            <div className="rangee rangee--serree">
              <Bouton type="submit" apparence="danger" charge={occupe} desactive={date === ''}>
                {t.foyerAnnoncerDepart}
              </Bouton>
            </div>
          </form>
        ) : (
          <div className="pile">
            <p className="statut-point statut-point--attente">
              {t.foyerDepartAnnonce} {jour(langue, monDepart.effectiveOn)}
            </p>
            {monDepart.relistedListingId !== null && <p className="secondaire">{t.foyerDepartBrouillon}</p>}
            <div>
              <Bouton apparence="discret" charge={occupe} onClick={() => void agir(() => annulerDepart(monDepart.id, 'changed_mind'))}>
                {t.foyerAnnulerDepart}
              </Bouton>
            </div>
          </div>
        )}
      </Bloc>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Utilitaires
// ---------------------------------------------------------------------------

/** Une date au format que l'API attend, sans fuseau : `DateOnly` côté serveur. */
function iso(date: Date): string {
  return date.toISOString().slice(0, 10)
}

function code(cause: unknown): string {
  return cause instanceof ErreurApi ? cause.code : 'internal'
}

function initiales(nom: string | null): string {
  if (nom === null) return '?'

  return nom
    .split(/\s+/)
    .filter((mot) => mot.length > 0)
    .slice(0, 2)
    .map((mot) => mot.charAt(0).toLocaleUpperCase())
    .join('')
}
